import { LegalDoc, LegalDocSet } from '../legal-doc.model';

/**
 * Privacy policy for ScreenDeck. Moved out of the ScreenDeck SPA (formerly
 * features/legal/datenschutz/datenschutz.content.ts) so the group keeps a single
 * published version; the SPA links here.
 *
 * Revision 04.08.2026 — Regulation (EU) 2024/1689 (AI Act) became generally
 * applicable on 02.08.2026, so its Art. 50 transparency duties are now in force
 * rather than upcoming. Newly documented: the QR-code scan analytics of the
 * tracker app, which processes device data of members of the public.
 */

const DE: LegalDoc = {
    title: 'Datenschutzerklärung ScreenDeck',
    meta: 'Stand: 4. August 2026 · Gültig für die Digital-Signage-Plattform ScreenDeck (Web-App, Abspielclient, QR-Code-Weiterleitung)',
    tocHeading: 'Inhalt',
    footer: 'Massgeblich ist die deutsche Fassung. Übersetzungen dienen der Verständlichkeit.',
    sections: [
        {
            num: '01',
            title: 'Verantwortliche Stelle',
            body: `
        <p>Verantwortliche im Sinne des Schweizer Datenschutzgesetzes (DSG) und, soweit anwendbar, der
        Datenschutz-Grundverordnung (DSGVO) ist:</p>
        <p><strong>Gastro Management Oberbillig</strong><br>
        Einzelunternehmen, Inhaber: Erik Alexander Oberbillig<br>
        Betreiberin der Plattform <strong>ScreenDeck</strong><br>
        Kirchenrütti 14, 6463 Bürglen UR, Schweiz</p>
        <p>Datenschutzanfragen zu ScreenDeck: <a href="mailto:privacy@screendeck.ch">privacy&#64;screendeck.ch</a><br>
        Zentrale Datenschutzstelle der Gruppe: <a href="mailto:privacy@gastro-management.ch">privacy&#64;gastro-management.ch</a></p>
        <p>Eine Datenschutzberaterin oder ein Datenschutzberater nach Art. 10 DSG ist nicht bezeichnet; ein
        Datenschutzbeauftragter nach Art. 37 DSGVO ist nicht bestellt, da die gesetzlichen Voraussetzungen
        nicht erfüllt sind. Anfragen richten Sie bitte an die oben genannten Adressen.</p>
      `,
        },
        {
            num: '02',
            title: 'Rollenverteilung',
            body: `
        <p>ScreenDeck ist eine mandantenfähige Software-as-a-Service-Lösung. Für die datenschutzrechtliche
        Einordnung ist zu unterscheiden:</p>
        <ul>
          <li><strong>Wir als Verantwortliche:</strong> für die Konto- und Vertragsverwaltung, den Betrieb,
              die Sicherheit und die Weiterentwicklung der Plattform sowie für unsere eigene
              Aussenkommunikation.</li>
          <li><strong>Wir als Auftragsbearbeiterin:</strong> für alle Daten, die eine Kundin oder ein Kunde
              (nachfolgend «Mandant») in ScreenDeck einstellt oder über ScreenDeck erhebt — insbesondere
              Anzeigeinhalte und die Statistik zu QR-Code-Scans nach Ziff. 08. Verantwortliche Stelle ist
              insoweit der Mandant; wir handeln nach dessen Weisung auf Grundlage eines
              Auftragsbearbeitungsvertrags (Art. 9 DSG / Art. 28 DSGVO).</li>
        </ul>
        <p>Ein Auftragsbearbeitungsvertrag wird auf Anfrage geschlossen und ist Bestandteil der
        Kundenbeziehung: <a href="mailto:privacy@screendeck.ch">privacy&#64;screendeck.ch</a></p>
      `,
        },
        {
            num: '03',
            title: 'Übersicht der verarbeiteten Daten',
            body: `
        <ul>
          <li><strong>Identifikationsdaten:</strong> Auth0-Benutzer-ID, E-Mail-Adresse.</li>
          <li><strong>Präferenzdaten:</strong> bevorzugte Sprache, Standardmandant, Designeinstellung.</li>
          <li><strong>Organisationsdaten:</strong> Mandantenzuordnung(en) und Rolle (Admin oder Nutzer).</li>
          <li><strong>Inhaltsbezogene Protokolldaten:</strong> beim Erstellen und Bearbeiten von Slides wird
              die E-Mail-Adresse der handelnden Person als Prüfangabe gespeichert
              (<code>CreatedByEmail</code>, <code>UpdatedByEmail</code>).</li>
          <li><strong>Betriebsprotokolle:</strong> technisches Operationsprotokoll mit Korrelations-ID,
              Operationstyp sowie Erfolg oder Fehler.</li>
          <li><strong>Geräte- und Statusdaten der Bildschirme:</strong> Kopplungsstatus, zuletzt gemeldeter
              Zeitpunkt, angezeigte Playlist, Bildschirmauflösung, Software-Version.</li>
          <li><strong>Scan-Ereignisdaten:</strong> Geräte- und Browserangaben von Personen, die einen auf
              einem Bildschirm angezeigten QR-Code scannen (Ziff. 08).</li>
          <li><strong>Support-Chat:</strong> Betreff, Nachrichten, Absenderadresse und Dateianhänge.</li>
          <li><strong>KI-Nutzungsdaten:</strong> Anzahl der KI-Anfragen je Nutzerin oder Nutzer und Mandant
              pro Monat (<code>YearMonth</code>, <code>UsageCount</code>) zur Verwaltung der Kontingente.</li>
        </ul>
        <p><strong>Herkunft:</strong> Die Daten stammen (a) von Ihnen selbst bei Registrierung und Nutzung,
        (b) automatisch aus technischen Systemen (Verbindungs-, Protokoll- und Gerätedaten), (c) bei
        Anmeldung über einen Drittanbieter (Google, Microsoft) von diesem Anbieter und (d) bei
        QR-Code-Scans vom Endgerät der scannenden Person.</p>
        <p><strong>Erforderlichkeit:</strong> Eine gültige E-Mail-Adresse ist für die Nutzung der Plattform
        erforderlich; ohne sie kann kein Konto geführt werden. Alle weiteren Angaben sind freiwillig.</p>
        <p><strong>Keine besonders schützenswerten Daten:</strong> Wir erheben keine besonders
        schützenswerten Personendaten im Sinne von Art. 5 lit. c DSG bzw. Art. 9 DSGVO. Mandanten sind
        gehalten, solche Daten auch nicht in Anzeigeinhalte oder Freitextfelder einzutragen.</p>
      `,
        },
        {
            num: '04',
            title: 'Anmeldung und Authentifizierung',
            body: `
        <p>Die Anmeldung erfolgt über den Identitätsdienst <strong>Auth0</strong> (Okta, Inc., USA).
        Auth0 verarbeitet dabei Ihre E-Mail-Adresse, vergibt eine eindeutige Benutzer-ID und kann zu
        Sicherheitszwecken Sitzungsdaten, IP-Adressen und Browserangaben speichern. Die Kommunikation
        erfolgt über OIDC/OAuth 2.0. <strong>ScreenDeck speichert keine Passwörter.</strong></p>
        <p>Ist die E-Mail-Adresse nicht im Zugriffstoken enthalten — etwa bei der Anmeldung über Google —,
        rufen wir sie serverseitig über die Auth0 Management API ab. Dieser Zugriff erfolgt über eine
        Maschine-zu-Maschine-Anwendung mit ausschliesslich lesender Berechtigung
        (<code>read:users</code>).</p>
        <p>Datenschutzhinweise von Okta:
        <a href="https://www.okta.com/privacy-policy/" target="_blank" rel="noopener noreferrer">okta.com/privacy-policy</a></p>
        <p class="ds-legal-basis">Rechtsgrundlage: Vertragserfüllung (Art. 31 Abs. 2 lit. a DSG / Art. 6 Abs. 1 lit. b DSGVO).</p>
      `,
        },
        {
            num: '05',
            title: 'Kontoeinstellungen, Mandantenzuordnung und Rollen',
            body: `
        <p>Ihre Spracheinstellung, Ihr Standardmandant und Ihre Designeinstellung werden Ihrer Auth0-ID
        zugeordnet in einer Azure-SQL-Datenbank gespeichert und ausschliesslich zur Personalisierung der
        Oberfläche verwendet. Sie können sie jederzeit in der Anwendung ändern.</p>
        <p>Für die Nutzung sind Sie mindestens einem Mandanten zugeordnet. Gespeichert werden Auth0-ID,
        Mandanten-ID und Rolle (<em>Admin</em> oder <em>User</em>). Diese Zuordnung ist für den Betrieb
        zwingend erforderlich und steuert, welche Daten Sie sehen und bearbeiten dürfen.</p>
        <p class="ds-legal-basis">Rechtsgrundlage: Vertragserfüllung (Art. 31 Abs. 2 lit. a DSG / Art. 6 Abs. 1 lit. b DSGVO).</p>
      `,
        },
        {
            num: '06',
            title: 'Anzeigeinhalte (Screens, Playlists, Slides)',
            body: `
        <p>Screens, Playlists und Slides werden je Mandant getrennt in einer Azure-SQL-Datenbank gespeichert;
        HTML-Inhalte und Medien liegen zusätzlich im Azure Blob Storage. Für die Vorschaubilder in der
        Verwaltungsoberfläche wird der Slide serverseitig gerendert und als Bild abgelegt.</p>
        <p>Anzeigeinhalte enthalten in der Regel keine Personendaten. Werden dennoch Personendaten
        eingestellt — etwa Fotos oder Namen von Mitarbeitenden auf einer Begrüssungstafel —, ist der
        Mandant dafür verantwortlich, dass er dazu berechtigt ist und die betroffenen Personen informiert
        hat. Wir verarbeiten solche Inhalte ausschliesslich als Auftragsbearbeiterin (Ziff. 02).</p>
        <p>Bei jedem Erstellen und Bearbeiten wird die E-Mail-Adresse der handelnden Person als Prüfangabe
        auf dem Slide gespeichert. Diese Angabe dient der internen Nachvollziehbarkeit von Änderungen und
        ist nur innerhalb des Mandanten sichtbar.</p>
        <p class="ds-legal-basis">Rechtsgrundlage: Vertragserfüllung sowie berechtigtes Interesse an
        nachvollziehbaren Änderungen (Art. 31 Abs. 1 und 2 DSG / Art. 6 Abs. 1 lit. b und f DSGVO).</p>
      `,
        },
        {
            num: '07',
            title: 'Abspielgeräte und Bildschirmstatus',
            body: `
        <p>Der Abspielclient läuft im Browser- oder Kioskmodus auf einem vom Mandanten bereitgestellten
        Gerät. Damit ein Bildschirm die richtigen Inhalte erhält und Störungen erkennbar sind, verarbeiten
        wir: Kopplungscode und Geräte-Kennung, Zeitpunkt der letzten Rückmeldung, aktuell angezeigte
        Playlist, Bildschirmauflösung, Software-Version sowie technische Fehlermeldungen.</p>
        <p>Diese Daten beziehen sich auf das Gerät, nicht auf eine bestimmte Person. Ein Personenbezug
        entsteht nur, soweit ein Gerät im Einzelfall einer Person zugeordnet werden kann.</p>
        <p>Zur Fernwartung kann eine berechtigte Person des Mandanten oder unser Support einen Bildschirm
        neu laden, neu koppeln oder dessen Inhalt wechseln. Solche Eingriffe werden protokolliert.
        <strong>Eine Fernbetrachtung des Bildschirminhalts über Kamera oder Bildschirmaufzeichnung des
        Aufstellorts findet nicht statt.</strong></p>
        <p class="ds-legal-basis">Rechtsgrundlage: Vertragserfüllung sowie berechtigtes Interesse am
        störungsfreien Betrieb (Art. 31 Abs. 1 und 2 DSG / Art. 6 Abs. 1 lit. b und f DSGVO).</p>
      `,
        },
        {
            num: '08',
            title: 'QR-Code-Weiterleitung und Anzeigestatistik',
            body: `
        <p>Mandanten können auf einem Slide einen QR-Code einblenden. Wird dieser gescannt, öffnet sich
        zunächst eine kurze Weiterleitungsseite von ScreenDeck und anschliessend das vom Mandanten
        hinterlegte Ziel. Dabei wird ein Scan-Ereignis erfasst mit:</p>
        <ul>
          <li>Kennung des Slides und des Bildschirms sowie eine zufällige Ereignis-ID zur Entdoppelung</li>
          <li>Zeitpunkt des Scans</li>
          <li>User-Agent, Spracheinstellung und Zeitzone des Endgeräts</li>
          <li>Bildschirmauflösung, Plattformkennung und Verbindungsart des Endgeräts</li>
          <li>Referrer und aufgerufene Adresse</li>
        </ul>
        <p><strong>Diese Angaben stammen von den Endgeräten von Gästen und Passanten, nicht von
        Kontoinhaberinnen und Kontoinhabern.</strong> Wir erheben dabei weder Namen noch Kontaktangaben
        und setzen für die Weiterleitung keine Cookies. Die Angaben werden ausschliesslich zu einer
        Reichweiten- und Gerätestatistik je Slide und Bildschirm zusammengefasst; eine Zusammenführung zu
        einem Profil einer einzelnen Person, ein Wiedererkennen über mehrere Scans hinweg oder eine
        Weitergabe zu Werbezwecken findet nicht statt.</p>
        <p>Verantwortliche Stelle für diese Statistik ist der Mandant, der den QR-Code einsetzt; wir handeln
        als Auftragsbearbeiterin (Ziff. 02). Die Kombination aus User-Agent, Auflösung, Sprache und
        Zeitzone kann ein Endgerät technisch unterscheidbar machen. Mandanten, die QR-Codes einsetzen,
        haben daher am Aufstellort auf die Statistik hinzuweisen und, soweit nach dem an ihrem Standort
        geltenden Recht erforderlich, eine Einwilligung einzuholen. Wir stellen die Funktion auf Wunsch
        mandantenweit ab.</p>
        <p class="ds-legal-basis">Rechtsgrundlage: berechtigtes Interesse des Mandanten an der Messung der
        Wirksamkeit seiner Anzeigen (Art. 31 Abs. 1 DSG / Art. 6 Abs. 1 lit. f DSGVO); soweit nach lokalem
        Recht erforderlich, Einwilligung (Art. 6 Abs. 1 lit. a DSGVO).</p>
      `,
        },
        {
            num: '09',
            title: 'Keine Erfassung von Personen vor dem Bildschirm',
            body: `
        <p>ScreenDeck erfasst keine Personen im Umfeld eines Bildschirms. Es werden weder Kameras noch
        Mikrofone noch Sensoren zur Zählung, Wiedererkennung oder Vermessung von Publikum eingesetzt.
        Insbesondere findet <strong>keine</strong> Gesichtserkennung, <strong>keine</strong> biometrische
        Kategorisierung und <strong>keine</strong> Emotionserkennung statt.</p>
        <p>Diese Feststellung ist auch mit Blick auf Art. 5 der Verordnung (EU) 2024/1689 (KI-Verordnung)
        bedeutsam, der unter anderem die biometrische Kategorisierung und die Emotionserkennung am
        Arbeitsplatz verbietet. Die entsprechenden Verbote gelten seit dem 2. Februar 2025. ScreenDeck
        bietet keine Funktion an, die davon erfasst wäre.</p>
      `,
        },
        {
            num: '10',
            title: 'KI-Assistent und KI-Verordnung',
            body: `
        <p>ScreenDeck stellt einen <strong>optionalen</strong> KI-Assistenten zur Verfügung, der beim
        Entwerfen und Umformulieren von Slide-Inhalten unterstützt. Wird er genutzt, übermitteln wir den
        Slide-Typ, Ihre Eingabe und den gegebenenfalls bereits vorhandenen Slide-Inhalt an den
        <strong>Azure OpenAI Service</strong> von Microsoft. Die Verarbeitung erfolgt in europäischen
        Rechenzentren.</p>
        <p>Microsoft verwendet die übermittelten Inhalte nach den Azure-OpenAI-Bedingungen
        <strong>nicht</strong> zum Training von Modellen. Einzelheiten:
        <a href="https://learn.microsoft.com/legal/cognitive-services/openai/data-privacy" target="_blank" rel="noopener noreferrer">Azure OpenAI Data &amp; Privacy</a>.
        Serverseitig speichern wir weder Ihre Eingaben noch die erzeugten Antworten dauerhaft; erfasst wird
        lediglich die monatliche Anzahl der Anfragen je Nutzerin oder Nutzer und Mandant zur Verwaltung der
        Kontingente.</p>
        <p><strong>Einstufung nach der KI-Verordnung.</strong> Die Verordnung (EU) 2024/1689 ist seit dem
        2. August 2026 allgemein anwendbar. Der KI-Assistent ist ein Werkzeug zur Inhaltsunterstützung und
        fällt weder unter die verbotenen Praktiken nach Art. 5 noch unter die Hochrisiko-Anwendungen nach
        Anhang III. Es gelten die Transparenzpflichten nach Art. 50:</p>
        <ul>
          <li><strong>Kenntlichmachung der Interaktion (Art. 50 Abs. 1):</strong> Der Assistent ist in der
              Oberfläche ausdrücklich als KI-Funktion bezeichnet und weist vor der Eingabe darauf hin, dass
              der Inhalt von einer KI erzeugt wird; Sie wissen jederzeit, dass Sie mit einem KI-System
              arbeiten.</li>
          <li><strong>Menschliche Freigabe:</strong> Erzeugte Inhalte werden nie selbsttätig veröffentlicht.
              Sie werden erst nach ausdrücklicher Übernahme und Freigabe durch eine berechtigte Person auf
              einem Bildschirm angezeigt. Die inhaltliche Verantwortung verbleibt beim Mandanten
              (AGB § 7).</li>
          <li><strong>Kennzeichnung erzeugter Inhalte (Art. 50 Abs. 2):</strong> Soweit erzeugte Inhalte
              über eine blosse Unterstützung bei der Textbearbeitung hinausgehen, sind sie maschinenlesbar
              als künstlich erzeugt zu kennzeichnen. Wir setzen diese Kennzeichnung derzeit um; bis zu
              ihrer Aktivierung weisen wir Mandanten an dieser Stelle ausdrücklich darauf hin, dass mit dem
              Assistenten erstellte Slides KI-erzeugte Inhalte enthalten können.</li>
        </ul>
        <p>Nach Art. 4 der KI-Verordnung sorgen wir dafür, dass die mit dem Betrieb und der Bereitstellung
        des Assistenten befassten Personen über ein ausreichendes Mass an KI-Kompetenz verfügen. Der
        entsprechende Nachweis wird intern geführt und Mandanten auf Anfrage zur Verfügung gestellt.</p>
        <p class="ds-legal-basis">Rechtsgrundlage: Vertragserfüllung (Art. 31 Abs. 2 lit. a DSG /
        Art. 6 Abs. 1 lit. b DSGVO). Microsoft ist insoweit als Unterauftragsbearbeiter eingebunden
        (Art. 9 Abs. 3 DSG / Art. 28 Abs. 4 DSGVO).</p>
      `,
        },
        {
            num: '11',
            title: 'Support-Chat',
            body: `
        <p>Über den integrierten Support-Chat verarbeiten wir E-Mail-Adresse und Auth0-ID der anfragenden
        Person, Betreff und Nachrichten der Konversation, hochgeladene Anhänge (Bilder, PDF) sowie
        Zeitstempel und Status der Konversation. Die Speicherung erfolgt im Azure Table Storage und im
        Azure Blob Storage.</p>
        <p>Benachrichtigungen über neue Anfragen und Antworten versenden wir per E-Mail über
        <strong>Azure Communication Services</strong>; dabei werden Adresse und Nachrichteninhalt an
        Microsoft übermittelt.</p>
        <p>Bitte übermitteln Sie im Support-Chat keine besonders schützenswerten Personendaten und keine
        Zugangsdaten. Wir fordern niemals Passwörter an.</p>
        <p class="ds-legal-basis">Rechtsgrundlage: Vertragserfüllung und Supportleistung
        (Art. 31 Abs. 2 lit. a DSG / Art. 6 Abs. 1 lit. b DSGVO).</p>
      `,
        },
        {
            num: '12',
            title: 'Echtzeitkommunikation',
            body: `
        <p>Statusänderungen erreichen die Verwaltungsoberfläche über den <strong>Azure SignalR
        Service</strong>, die einzelnen Bildschirme über <strong>Azure Web PubSub</strong>. Beide Dienste
        verarbeiten zur Vermittlung vorübergehend Verbindungsdaten wie Sitzungskennungen und IP-Adressen.
        Inhalte werden dort nicht dauerhaft gespeichert.</p>
        <p class="ds-legal-basis">Rechtsgrundlage: Vertragserfüllung (Art. 31 Abs. 2 lit. a DSG / Art. 6 Abs. 1 lit. b DSGVO).</p>
      `,
        },
        {
            num: '13',
            title: 'Infrastruktur und Auftragsbearbeiter',
            body: `
        <p>ScreenDeck wird auf <strong>Microsoft Azure</strong> betrieben. Eingesetzt werden:</p>
        <ul>
          <li><strong>Azure SQL Database</strong> — dauerhafte Datenhaltung</li>
          <li><strong>Azure Blob Storage</strong> — Medien, HTML-Inhalte, Vorschaubilder, Support-Anhänge</li>
          <li><strong>Azure Table Storage</strong> — Bildschirmstatus, Support-Konversationen, Scan-Statistik, Betriebsprotokolle</li>
          <li><strong>Azure Functions</strong> — serverseitige Verarbeitung</li>
          <li><strong>Azure Service Bus</strong> — asynchrone Weitergabe von Datenänderungen</li>
          <li><strong>Azure SignalR Service / Web PubSub</strong> — Echtzeitkommunikation</li>
          <li><strong>Azure OpenAI Service</strong> — KI-Assistent (Ziff. 10), europäische Rechenzentren</li>
          <li><strong>Azure Communication Services</strong> — E-Mail-Versand (Ziff. 11)</li>
        </ul>
        <p>Weitere Auftragsbearbeiter:</p>
        <ul>
          <li><strong>Okta, Inc. (Auth0), USA</strong> — Authentifizierung (Ziff. 04)</li>
          <li><strong>Google Ireland Ltd., Irland</strong> — Reichweitenmessung der Verwaltungsoberfläche,
              nur mit Ihrer Einwilligung (Ziff. 17) —
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">policies.google.com/privacy</a></li>
        </ul>
        <p>Microsoft handelt für die Datenhaltung als Auftragsbearbeiterin:
        <a href="https://privacy.microsoft.com/de-de/privacystatement" target="_blank" rel="noopener noreferrer">privacy.microsoft.com</a>.
        Die Rechnungsstellung erfolgt über ein getrenntes Abrechnungssystem der Gruppe; ScreenDeck selbst
        speichert keine Zahlungsdaten.</p>
        <p>Eine vollständige, laufend nachgeführte Liste der Unterauftragsbearbeiter mit Zweck,
        Datenkategorien und Bearbeitungsort stellen wir auf Anfrage zur Verfügung. Neue
        Unterauftragsbearbeiter kündigen wir Mandanten vorgängig an; ein Widerspruchsrecht nach dem
        Auftragsbearbeitungsvertrag bleibt vorbehalten. Ein Verkauf von Personendaten findet nicht statt.</p>
      `,
        },
        {
            num: '14',
            title: 'Bearbeitung im Ausland',
            body: `
        <p>Die Datenhaltung erfolgt vorrangig in europäischen Azure-Regionen (West Europe / North Europe).
        Einzelne der in Ziff. 13 genannten Anbieter haben ihren Sitz in den <strong>USA</strong>; ein
        Zugriff aus den USA im Rahmen von Support und Wartung lässt sich nicht vollständig ausschliessen.</p>
        <p>Die Übermittlung stützt sich auf:</p>
        <ul>
          <li>die <strong>Standardvertragsklauseln</strong> der EU-Kommission (Art. 46 Abs. 2 lit. c DSGVO)
              in Verbindung mit Art. 16 Abs. 2 lit. d DSG, ergänzt um die für die Schweiz erforderlichen
              Anpassungen gemäss Anerkennung des EDÖB;</li>
          <li>ergänzend den Angemessenheitsbeschluss zum <strong>EU–U.S. Data Privacy Framework</strong>
              sowie die Schweizer Anerkennung des <strong>Swiss–U.S. Data Privacy Framework</strong>,
              soweit der jeweilige Anbieter zertifiziert ist.</li>
        </ul>
        <p>Wir stützen uns bewusst nicht allein auf das Data Privacy Framework, sondern führen die
        Standardvertragsklauseln als eigenständige Grundlage mit, damit die Übermittlung auch bei einem
        Wegfall des Angemessenheitsbeschlusses abgesichert bleibt. Eine Kopie der massgeblichen Garantien
        stellen wir auf Anfrage zur Verfügung:
        <a href="mailto:privacy@screendeck.ch">privacy&#64;screendeck.ch</a></p>
      `,
        },
        {
            num: '15',
            title: 'Aufbewahrung und Löschung',
            body: `
        <ul>
          <li><strong>Kontodaten:</strong> für die Dauer des Kontos; nach Deaktivierung oder Kündigung
              Löschung innerhalb von <strong>3 Monaten</strong>, soweit keine gesetzliche
              Aufbewahrungspflicht entgegensteht.</li>
          <li><strong>Anzeigeinhalte:</strong> bis zur Löschung durch den Mandanten, längstens bis 30 Tage
              nach Vertragsende; danach Löschung oder Rückgabe nach Weisung des Mandanten.</li>
          <li><strong>Betriebsprotokolle:</strong> längstens <strong>90 Tage</strong>.</li>
          <li><strong>Bildschirmstatusdaten:</strong> rollierend, längstens <strong>90 Tage</strong>.</li>
          <li><strong>Scan-Statistik (Ziff. 08):</strong> Einzelereignisse längstens <strong>14 Monate</strong>,
              danach nur noch verdichtete Kennzahlen ohne Gerätemerkmale.</li>
          <li><strong>Support-Konversationen:</strong> <strong>12 Monate</strong> nach Abschluss.</li>
          <li><strong>Belege der Rechnungsstellung:</strong> 10 Jahre (Art. 958f OR).</li>
        </ul>
        <p>Ist eine sofortige Löschung technisch nicht möglich — etwa in Sicherungskopien —, sperren wir
        die betroffenen Daten und löschen sie mit dem nächsten regulären Sicherungszyklus.</p>
      `,
        },
        {
            num: '16',
            title: 'Datensicherheit',
            body: `
        <p>Wir treffen angemessene technische und organisatorische Massnahmen nach Art. 8 DSG und Art. 32
        DSGVO, insbesondere:</p>
        <ul>
          <li>durchgehende Transportverschlüsselung (TLS) und Verschlüsselung ruhender Daten</li>
          <li>rollenbasierte Zugriffssteuerung und Trennung der Mandantendaten auf Datenebene</li>
          <li>private Speicher-Container ohne anonymen Zugriff; Auslieferung nur über zeitlich befristete,
              signierte Adressen</li>
          <li>Authentifizierung ausschliesslich über Auth0 (OAuth 2.0 / OIDC), keine eigene
              Passwortverwaltung</li>
          <li>protokollierte Verwaltungszugriffe und regelmässige Sicherungen mit definierten
              Wiederherstellungszielen</li>
        </ul>
        <p>Verletzungen der Datensicherheit melden wir dem EDÖB so rasch als möglich (Art. 24 DSG) und,
        soweit die DSGVO anwendbar ist, der zuständigen Aufsichtsbehörde innert 72 Stunden (Art. 33 DSGVO).
        Betroffene Mandanten informieren wir unverzüglich, damit sie ihren eigenen Meldepflichten
        nachkommen können.</p>
      `,
        },
        {
            num: '17',
            title: 'Cookies und lokaler Speicher',
            body: `
        <p>In der Verwaltungsoberfläche verwenden wir den lokalen Speicher des Browsers:</p>
        <ul>
          <li><strong>Notwendig (immer aktiv):</strong> Authentifizierungstoken von Auth0 sowie die
              zuletzt gewählte Organisation (<code>screendeck.activeTenantId</code>), damit Ihre Sitzung
              einen Seitenwechsel überdauert.</li>
          <li><strong>Funktional (nur mit Einwilligung):</strong> Spracheinstellung
              (<code>screendeck.language</code>) und Designeinstellung (<code>screendeck.theme</code>).</li>
          <li><strong>Statistik (nur mit Einwilligung):</strong> Google Analytics zur Auswertung der
              Nutzung der Verwaltungsoberfläche mit gekürzter IP-Adresse.</li>
        </ul>
        <p>Beim ersten Aufruf erscheint ein Einwilligungsbanner. Ihre Auswahl wird unter
        <code>screendeck.cookie_consent</code> gespeichert und ist jederzeit in den Profileinstellungen
        unter «Cookie-Einstellungen» änderbar. Werbe-Cookies und Cookies zur Bildung persönlicher Profile
        setzen wir nicht ein. Auf der QR-Weiterleitungsseite (Ziff. 08) werden keine Cookies gesetzt.</p>
        <p class="ds-legal-basis">Rechtsgrundlage: Einwilligung für funktionale und statistische Zwecke
        (Art. 6 Abs. 1 lit. a DSGVO); Vertragserfüllung für technisch notwendige Speicherung
        (Art. 6 Abs. 1 lit. b DSGVO).</p>
      `,
        },
        {
            num: '18',
            title: 'Ihre Rechte',
            body: `
        <p>Sie haben im Rahmen des anwendbaren Rechts das Recht auf:</p>
        <ul>
          <li><strong>Auskunft</strong> über die zu Ihrer Person bearbeiteten Daten (Art. 25 DSG / Art. 15 DSGVO)</li>
          <li><strong>Berichtigung</strong> unrichtiger Daten (Art. 32 DSG / Art. 16 DSGVO)</li>
          <li><strong>Löschung</strong>, soweit keine Aufbewahrungspflicht entgegensteht (Art. 32 DSG / Art. 17 DSGVO)</li>
          <li><strong>Einschränkung</strong> der Bearbeitung (Art. 18 DSGVO)</li>
          <li><strong>Datenherausgabe und -übertragung</strong> in einem gängigen elektronischen Format
              (Art. 28 DSG / Art. 20 DSGVO)</li>
          <li><strong>Widerspruch</strong> gegen eine Bearbeitung, die auf einem berechtigten Interesse
              beruht (Art. 30 Abs. 2 lit. b DSG / Art. 21 DSGVO)</li>
          <li><strong>Widerruf einer Einwilligung</strong> jederzeit mit Wirkung für die Zukunft
              (Art. 6 Abs. 6 DSG / Art. 7 Abs. 3 DSGVO)</li>
        </ul>
        <p>Wenden Sie sich formlos an <a href="mailto:privacy@screendeck.ch">privacy&#64;screendeck.ch</a>.
        Wir antworten innert 30 Tagen. Zur Überprüfung Ihrer Identität können wir zusätzliche Angaben
        verlangen; diese verwenden wir ausschliesslich zu diesem Zweck.</p>
        <p><strong>Bei Daten eines Mandanten</strong> — insbesondere Anzeigeinhalten und Scan-Statistiken —
        sind wir Auftragsbearbeiterin. Wir leiten Ihr Begehren an den verantwortlichen Mandanten weiter
        und unterstützen ihn bei der Beantwortung. Bitte richten Sie solche Anliegen nach Möglichkeit
        direkt an den Betrieb, in dem Sie den Bildschirm gesehen haben.</p>
      `,
        },
        {
            num: '19',
            title: 'Beschwerderecht',
            body: `
        <p>Sie können sich beim <strong>Eidgenössischen Datenschutz- und Öffentlichkeitsbeauftragten
        (EDÖB)</strong>, Feldeggweg 1, 3003 Bern, beschweren:
        <a href="https://www.edoeb.admin.ch" target="_blank" rel="noopener noreferrer">www.edoeb.admin.ch</a></p>
        <p>Fällt die Bearbeitung unter die DSGVO, steht Ihnen zusätzlich das Recht auf Beschwerde bei der
        Aufsichtsbehörde Ihres Aufenthaltsorts, Arbeitsplatzes oder des Orts des mutmasslichen Verstosses
        zu (Art. 77 DSGVO); im Vereinigten Königreich beim Information Commissioner's Office.</p>
      `,
        },
        {
            num: '20',
            title: 'Keine automatisierte Entscheidung im Einzelfall',
            body: `
        <p>ScreenDeck trifft keine ausschliesslich automatisierten Einzelentscheidungen einschliesslich
        Profiling, die für Sie eine rechtliche Wirkung entfalten oder Sie in vergleichbarer Weise erheblich
        beeinträchtigen (Art. 21 DSG / Art. 22 DSGVO). Die Steuerung, welcher Inhalt auf welchem Bildschirm
        erscheint, folgt ausschliesslich den vom Mandanten gesetzten Regeln (Zeitplan, Reihenfolge,
        Gültigkeitsdauer) und wertet keine Merkmale von Personen aus.</p>
      `,
        },
        {
            num: '21',
            title: 'Änderungen dieser Erklärung',
            body: `
        <p>Wir überprüfen diese Erklärung regelmässig und passen sie an, wenn sich die Rechtslage oder
        unsere Bearbeitungen ändern. Die jeweils geltende Fassung ist unter
        <a href="https://www.gastro-management.ch/datenschutz#screendeck">gastro-management.ch/datenschutz</a>
        abrufbar. Über wesentliche Änderungen informieren wir registrierte Nutzerinnen und Nutzer per
        E-Mail oder durch einen deutlichen Hinweis in der Anwendung. Das Datum der letzten Überarbeitung
        steht im Seitenkopf.</p>
      `,
        },
    ],
};

const EN: LegalDoc = {
    title: 'ScreenDeck Privacy Policy',
    meta: 'As at 4 August 2026 · Applies to the ScreenDeck digital signage platform (web app, player client, QR code redirect)',
    tocHeading: 'Contents',
    footer: 'The German version prevails. Translations are provided for convenience.',
    sections: [
        {
            num: '01',
            title: 'Controller',
            body: `
        <p>The controller within the meaning of the Swiss Federal Act on Data Protection (FADP) and, where
        applicable, the General Data Protection Regulation (GDPR) is:</p>
        <p><strong>Gastro Management Oberbillig</strong><br>
        Sole proprietorship, owner: Erik Alexander Oberbillig<br>
        Operator of the <strong>ScreenDeck</strong> platform<br>
        Kirchenrütti 14, 6463 Bürglen UR, Switzerland</p>
        <p>Privacy enquiries about ScreenDeck: <a href="mailto:privacy@screendeck.ch">privacy&#64;screendeck.ch</a><br>
        Central privacy contact for the group: <a href="mailto:privacy@gastro-management.ch">privacy&#64;gastro-management.ch</a></p>
        <p>No data protection adviser under Art. 10 FADP has been designated and no data protection officer
        under Art. 37 GDPR has been appointed, as the statutory conditions are not met. Please direct
        enquiries to the addresses above.</p>
      `,
        },
        {
            num: '02',
            title: 'Allocation of roles',
            body: `
        <p>ScreenDeck is a multi-tenant software-as-a-service solution. Two roles must be distinguished:</p>
        <ul>
          <li><strong>We act as controller</strong> for account and contract administration, for operating,
              securing and developing the platform, and for our own external communications.</li>
          <li><strong>We act as processor</strong> for all data a customer (the "tenant") places into
              ScreenDeck or collects through it — in particular display content and the QR code scan
              statistics under section 08. For that data the tenant is the controller and we act on its
              instructions under a data processing agreement (Art. 9 FADP / Art. 28 GDPR).</li>
        </ul>
        <p>A data processing agreement is concluded on request and forms part of the customer relationship:
        <a href="mailto:privacy@screendeck.ch">privacy&#64;screendeck.ch</a></p>
      `,
        },
        {
            num: '03',
            title: 'Overview of the data we process',
            body: `
        <ul>
          <li><strong>Identification data:</strong> Auth0 user ID, email address.</li>
          <li><strong>Preferences:</strong> language, default tenant, appearance setting.</li>
          <li><strong>Organisation data:</strong> tenant assignment(s) and role (admin or user).</li>
          <li><strong>Content audit data:</strong> when a slide is created or edited, the acting person's
              email address is recorded (<code>CreatedByEmail</code>, <code>UpdatedByEmail</code>).</li>
          <li><strong>Operation logs:</strong> technical log with correlation ID, operation type and
              success or error.</li>
          <li><strong>Screen device and status data:</strong> pairing status, last check-in, playlist on
              display, screen resolution, software version.</li>
          <li><strong>Scan event data:</strong> device and browser details of people who scan a QR code
              shown on a screen (section 08).</li>
          <li><strong>Support chat:</strong> subject, messages, sender address and attachments.</li>
          <li><strong>AI usage data:</strong> monthly number of AI requests per user and tenant
              (<code>YearMonth</code>, <code>UsageCount</code>) for quota management.</li>
        </ul>
        <p><strong>Sources:</strong> data comes (a) from you when registering and using the platform,
        (b) automatically from technical systems (connection, log and device data), (c) from your chosen
        identity provider (Google, Microsoft) when you sign in through it, and (d) from the device of a
        person scanning a QR code.</p>
        <p><strong>Necessity:</strong> a valid email address is required to use the platform; no account
        can be maintained without it. All other details are optional.</p>
        <p><strong>No sensitive data:</strong> we do not collect sensitive personal data within the meaning
        of Art. 5(c) FADP or Art. 9 GDPR. Tenants are required not to place such data into display content
        or free-text fields either.</p>
      `,
        },
        {
            num: '04',
            title: 'Sign-in and authentication',
            body: `
        <p>Sign-in is handled by the identity service <strong>Auth0</strong> (Okta, Inc., USA). Auth0
        processes your email address, assigns a unique user ID and may store session data, IP addresses and
        browser details for security purposes. Communication uses OIDC/OAuth 2.0.
        <strong>ScreenDeck stores no passwords.</strong></p>
        <p>Where the email address is not contained in the access token — for example when signing in with
        Google — we retrieve it server-side through the Auth0 Management API, using a machine-to-machine
        application with read-only permission (<code>read:users</code>).</p>
        <p>Okta's privacy notice:
        <a href="https://www.okta.com/privacy-policy/" target="_blank" rel="noopener noreferrer">okta.com/privacy-policy</a></p>
        <p class="ds-legal-basis">Legal basis: performance of a contract (Art. 31(2)(a) FADP / Art. 6(1)(b) GDPR).</p>
      `,
        },
        {
            num: '05',
            title: 'Account settings, tenant assignment and roles',
            body: `
        <p>Your language, default tenant and appearance settings are stored against your Auth0 ID in an
        Azure SQL database and used solely to personalise the interface. You can change them in the
        application at any time.</p>
        <p>To use the platform you are assigned to at least one tenant. We store your Auth0 ID, the tenant
        ID and your role (<em>Admin</em> or <em>User</em>). This assignment is strictly necessary for
        operation and governs which data you may see and edit.</p>
        <p class="ds-legal-basis">Legal basis: performance of a contract (Art. 31(2)(a) FADP / Art. 6(1)(b) GDPR).</p>
      `,
        },
        {
            num: '06',
            title: 'Display content (screens, playlists, slides)',
            body: `
        <p>Screens, playlists and slides are stored per tenant in an Azure SQL database; HTML content and
        media additionally reside in Azure Blob Storage. For the preview thumbnails in the management
        interface, a slide is rendered server-side and stored as an image.</p>
        <p>Display content generally contains no personal data. Where personal data is nevertheless placed
        into it — for instance photographs or names of staff on a welcome board — the tenant is responsible
        for being entitled to do so and for having informed the people concerned. We process such content
        exclusively as a processor (section 02).</p>
        <p>Each create and edit operation records the acting person's email address on the slide. This
        serves internal traceability of changes and is visible only within the tenant.</p>
        <p class="ds-legal-basis">Legal basis: performance of a contract and legitimate interest in
        traceable changes (Art. 31(1) and (2) FADP / Art. 6(1)(b) and (f) GDPR).</p>
      `,
        },
        {
            num: '07',
            title: 'Player devices and screen status',
            body: `
        <p>The player client runs in a browser or kiosk mode on a device provided by the tenant. So that a
        screen receives the right content and faults become visible, we process: pairing code and device
        identifier, time of last check-in, playlist currently on display, screen resolution, software
        version and technical error messages.</p>
        <p>This data relates to the device rather than to an identified person. A personal reference arises
        only where a device can be attributed to an individual in a particular case.</p>
        <p>For remote maintenance, an authorised person of the tenant or our support team can reload,
        re-pair or switch the content of a screen. Such actions are logged. <strong>There is no remote
        viewing of the installation site by camera or screen recording.</strong></p>
        <p class="ds-legal-basis">Legal basis: performance of a contract and legitimate interest in
        fault-free operation (Art. 31(1) and (2) FADP / Art. 6(1)(b) and (f) GDPR).</p>
      `,
        },
        {
            num: '08',
            title: 'QR code redirect and display statistics',
            body: `
        <p>Tenants can show a QR code on a slide. When it is scanned, a short ScreenDeck redirect page opens
        first and then the destination configured by the tenant. A scan event is recorded, consisting of:</p>
        <ul>
          <li>the slide and screen identifiers plus a random event ID used for de-duplication</li>
          <li>the time of the scan</li>
          <li>the user agent, language setting and time zone of the device</li>
          <li>the screen resolution, platform identifier and connection type of the device</li>
          <li>the referrer and the address called</li>
        </ul>
        <p><strong>This data originates from the devices of guests and passers-by, not from account
        holders.</strong> We collect neither names nor contact details and set no cookies for the redirect.
        The data is aggregated solely into reach and device statistics per slide and screen; it is not
        combined into a profile of an individual, not used to recognise anyone across several scans, and
        not passed on for advertising purposes.</p>
        <p>The controller for these statistics is the tenant using the QR code; we act as processor
        (section 02). The combination of user agent, resolution, language and time zone can make a device
        technically distinguishable. Tenants using QR codes must therefore give notice of the statistics at
        the installation site and, where the law applicable at their location requires it, obtain consent.
        On request we disable the feature for an entire tenant.</p>
        <p class="ds-legal-basis">Legal basis: the tenant's legitimate interest in measuring the
        effectiveness of its displays (Art. 31(1) FADP / Art. 6(1)(f) GDPR); consent where required by
        local law (Art. 6(1)(a) GDPR).</p>
      `,
        },
        {
            num: '09',
            title: 'No capture of people in front of the screen',
            body: `
        <p>ScreenDeck does not capture people around a screen. No cameras, microphones or sensors are used
        to count, recognise or measure an audience. In particular there is <strong>no</strong> facial
        recognition, <strong>no</strong> biometric categorisation and <strong>no</strong> emotion
        recognition.</p>
        <p>This also matters under Art. 5 of Regulation (EU) 2024/1689 (the AI Act), which prohibits
        biometric categorisation and emotion recognition in the workplace, among other practices. Those
        prohibitions have applied since 2 February 2025. ScreenDeck offers no function that would fall
        within them.</p>
      `,
        },
        {
            num: '10',
            title: 'AI assistant and the AI Act',
            body: `
        <p>ScreenDeck provides an <strong>optional</strong> AI assistant that helps draft and rephrase slide
        content. When it is used, we transmit the slide type, your input and any existing slide content to
        Microsoft's <strong>Azure OpenAI Service</strong>. Processing takes place in European data centres.</p>
        <p>Under the Azure OpenAI terms, Microsoft does <strong>not</strong> use the transmitted content to
        train models. Details:
        <a href="https://learn.microsoft.com/legal/cognitive-services/openai/data-privacy" target="_blank" rel="noopener noreferrer">Azure OpenAI Data &amp; Privacy</a>.
        We do not persistently store your inputs or the generated responses server-side; we record only the
        monthly number of requests per user and tenant for quota management.</p>
        <p><strong>Classification under the AI Act.</strong> Regulation (EU) 2024/1689 has been generally
        applicable since 2 August 2026. The AI assistant is a content support tool and falls neither within
        the prohibited practices of Art. 5 nor within the high-risk uses of Annex III. The transparency
        obligations of Art. 50 apply:</p>
        <ul>
          <li><strong>Disclosure of interaction (Art. 50(1)):</strong> the assistant is expressly labelled
              as an AI function in the interface and states before you type that the content will be
              generated by AI, so you always know you are working with an AI system.</li>
          <li><strong>Human release:</strong> generated content is never published automatically. It appears
              on a screen only after an authorised person has expressly adopted and released it.
              Responsibility for the content remains with the tenant (GTC § 7).</li>
          <li><strong>Marking of generated content (Art. 50(2)):</strong> where generated content goes
              beyond mere assistance with standard editing, it must be marked in machine-readable form as
              artificially generated. We are currently implementing that marking; until it is active we
              expressly note here that slides created with the assistant may contain AI-generated
              content.</li>
        </ul>
        <p>Under Art. 4 of the AI Act we ensure that the people involved in providing and operating the
        assistant have a sufficient level of AI literacy. The corresponding record is kept internally and
        made available to tenants on request.</p>
        <p class="ds-legal-basis">Legal basis: performance of a contract (Art. 31(2)(a) FADP /
        Art. 6(1)(b) GDPR). Microsoft is engaged as a sub-processor (Art. 9(3) FADP / Art. 28(4) GDPR).</p>
      `,
        },
        {
            num: '11',
            title: 'Support chat',
            body: `
        <p>Through the integrated support chat we process the requesting person's email address and Auth0
        ID, the subject and messages of the conversation, uploaded attachments (images, PDF) as well as
        timestamps and conversation status. Storage is in Azure Table Storage and Azure Blob Storage.</p>
        <p>We send notifications about new requests and replies by email through <strong>Azure
        Communication Services</strong>; the address and message content are transmitted to Microsoft.</p>
        <p>Please do not submit sensitive personal data or credentials through the support chat. We never
        ask for passwords.</p>
        <p class="ds-legal-basis">Legal basis: performance of a contract and provision of support
        (Art. 31(2)(a) FADP / Art. 6(1)(b) GDPR).</p>
      `,
        },
        {
            num: '12',
            title: 'Real-time communication',
            body: `
        <p>Status changes reach the management interface via <strong>Azure SignalR Service</strong> and the
        individual screens via <strong>Azure Web PubSub</strong>. For routing purposes both services
        temporarily process connection data such as session identifiers and IP addresses. Content is not
        persistently stored there.</p>
        <p class="ds-legal-basis">Legal basis: performance of a contract (Art. 31(2)(a) FADP / Art. 6(1)(b) GDPR).</p>
      `,
        },
        {
            num: '13',
            title: 'Infrastructure and processors',
            body: `
        <p>ScreenDeck runs on <strong>Microsoft Azure</strong>, using:</p>
        <ul>
          <li><strong>Azure SQL Database</strong> — persistent data storage</li>
          <li><strong>Azure Blob Storage</strong> — media, HTML content, preview images, support attachments</li>
          <li><strong>Azure Table Storage</strong> — screen status, support conversations, scan statistics, operation logs</li>
          <li><strong>Azure Functions</strong> — server-side processing</li>
          <li><strong>Azure Service Bus</strong> — asynchronous relay of data changes</li>
          <li><strong>Azure SignalR Service / Web PubSub</strong> — real-time communication</li>
          <li><strong>Azure OpenAI Service</strong> — AI assistant (section 10), European data centres</li>
          <li><strong>Azure Communication Services</strong> — email delivery (section 11)</li>
        </ul>
        <p>Further processors:</p>
        <ul>
          <li><strong>Okta, Inc. (Auth0), USA</strong> — authentication (section 04)</li>
          <li><strong>Google Ireland Ltd., Ireland</strong> — usage measurement of the management
              interface, only with your consent (section 17) —
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">policies.google.com/privacy</a></li>
        </ul>
        <p>Microsoft acts as a processor for the data storage:
        <a href="https://privacy.microsoft.com/en-us/privacystatement" target="_blank" rel="noopener noreferrer">privacy.microsoft.com</a>.
        Invoicing is handled by a separate billing system of the group; ScreenDeck itself stores no payment
        data.</p>
        <p>A complete and continuously maintained list of sub-processors, stating purpose, data categories
        and processing location, is available on request. We announce new sub-processors to tenants in
        advance; any right to object under the data processing agreement is reserved. We do not sell
        personal data.</p>
      `,
        },
        {
            num: '14',
            title: 'Processing abroad',
            body: `
        <p>Data is held primarily in European Azure regions (West Europe / North Europe). Some of the
        providers named in section 13 are established in the <strong>USA</strong>; access from the USA in
        the course of support and maintenance cannot be entirely ruled out.</p>
        <p>Transfers are based on:</p>
        <ul>
          <li>the European Commission's <strong>Standard Contractual Clauses</strong> (Art. 46(2)(c) GDPR)
              in conjunction with Art. 16(2)(d) FADP, with the amendments required for Switzerland as
              recognised by the FDPIC;</li>
          <li>additionally the adequacy decision on the <strong>EU–U.S. Data Privacy Framework</strong> and
              the Swiss recognition of the <strong>Swiss–U.S. Data Privacy Framework</strong>, where the
              provider concerned is certified.</li>
        </ul>
        <p>We deliberately do not rely on the Data Privacy Framework alone but maintain the Standard
        Contractual Clauses as an independent basis, so that transfers remain covered should the adequacy
        decision cease to apply. A copy of the relevant safeguards is available on request:
        <a href="mailto:privacy@screendeck.ch">privacy&#64;screendeck.ch</a></p>
      `,
        },
        {
            num: '15',
            title: 'Retention and deletion',
            body: `
        <ul>
          <li><strong>Account data:</strong> for the life of the account; after deactivation or termination
              deleted within <strong>3 months</strong>, unless a statutory retention duty applies.</li>
          <li><strong>Display content:</strong> until deleted by the tenant, at the latest 30 days after
              the contract ends; then deleted or returned as the tenant instructs.</li>
          <li><strong>Operation logs:</strong> at most <strong>90 days</strong>.</li>
          <li><strong>Screen status data:</strong> rolling, at most <strong>90 days</strong>.</li>
          <li><strong>Scan statistics (section 08):</strong> individual events at most
              <strong>14 months</strong>, thereafter only aggregated figures without device
              characteristics.</li>
          <li><strong>Support conversations:</strong> <strong>12 months</strong> after closure.</li>
          <li><strong>Accounting records:</strong> 10 years (Art. 958f CO).</li>
        </ul>
        <p>Where immediate deletion is technically impossible — for instance in backups — we block the data
        concerned and delete it with the next regular backup cycle.</p>
      `,
        },
        {
            num: '16',
            title: 'Data security',
            body: `
        <p>We take appropriate technical and organisational measures under Art. 8 FADP and Art. 32 GDPR, in
        particular:</p>
        <ul>
          <li>end-to-end transport encryption (TLS) and encryption of data at rest</li>
          <li>role-based access control and separation of tenant data at the data layer</li>
          <li>private storage containers without anonymous access; delivery only through time-limited,
              signed addresses</li>
          <li>authentication exclusively through Auth0 (OAuth 2.0 / OIDC), with no password store of our own</li>
          <li>logged administrative access and regular backups with defined recovery objectives</li>
        </ul>
        <p>We report data security breaches to the FDPIC as soon as possible (Art. 24 FADP) and, where the
        GDPR applies, to the competent supervisory authority within 72 hours (Art. 33 GDPR). We inform
        affected tenants without delay so that they can meet their own notification duties.</p>
      `,
        },
        {
            num: '17',
            title: 'Cookies and local storage',
            body: `
        <p>The management interface uses the browser's local storage:</p>
        <ul>
          <li><strong>Necessary (always active):</strong> Auth0 authentication tokens and the organisation
              last selected (<code>screendeck.activeTenantId</code>), so that your session survives a page
              change.</li>
          <li><strong>Functional (consent only):</strong> language (<code>screendeck.language</code>) and
              appearance (<code>screendeck.theme</code>).</li>
          <li><strong>Statistics (consent only):</strong> Google Analytics to evaluate use of the
              management interface, with a truncated IP address.</li>
        </ul>
        <p>A consent banner appears on first visit. Your choice is stored under
        <code>screendeck.cookie_consent</code> and can be changed at any time in the profile settings under
        "Cookie settings". We use no advertising cookies and no cookies that build personal profiles. The
        QR redirect page (section 08) sets no cookies.</p>
        <p class="ds-legal-basis">Legal basis: consent for functional and statistical purposes
        (Art. 6(1)(a) GDPR); performance of a contract for technically necessary storage
        (Art. 6(1)(b) GDPR).</p>
      `,
        },
        {
            num: '18',
            title: 'Your rights',
            body: `
        <p>Within the limits of the applicable law you have the right to:</p>
        <ul>
          <li><strong>access</strong> the data processed about you (Art. 25 FADP / Art. 15 GDPR)</li>
          <li><strong>rectification</strong> of inaccurate data (Art. 32 FADP / Art. 16 GDPR)</li>
          <li><strong>erasure</strong>, unless a retention duty applies (Art. 32 FADP / Art. 17 GDPR)</li>
          <li><strong>restriction</strong> of processing (Art. 18 GDPR)</li>
          <li><strong>data portability</strong> in a common electronic format (Art. 28 FADP / Art. 20 GDPR)</li>
          <li><strong>object</strong> to processing based on a legitimate interest
              (Art. 30(2)(b) FADP / Art. 21 GDPR)</li>
          <li><strong>withdraw consent</strong> at any time with effect for the future
              (Art. 6(6) FADP / Art. 7(3) GDPR)</li>
        </ul>
        <p>Contact us informally at <a href="mailto:privacy@screendeck.ch">privacy&#64;screendeck.ch</a>.
        We reply within 30 days. We may request additional details to verify your identity and use them for
        that purpose only.</p>
        <p><strong>For a tenant's data</strong> — in particular display content and scan statistics — we act
        as processor. We forward your request to the responsible tenant and support it in responding.
        Where possible, please address such matters directly to the business where you saw the screen.</p>
      `,
        },
        {
            num: '19',
            title: 'Right to lodge a complaint',
            body: `
        <p>You may lodge a complaint with the <strong>Federal Data Protection and Information Commissioner
        (FDPIC)</strong>, Feldeggweg 1, 3003 Bern:
        <a href="https://www.edoeb.admin.ch" target="_blank" rel="noopener noreferrer">www.edoeb.admin.ch</a></p>
        <p>Where the processing falls under the GDPR, you additionally have the right to complain to the
        supervisory authority of your habitual residence, place of work or the place of the alleged
        infringement (Art. 77 GDPR); in the United Kingdom, to the Information Commissioner's Office.</p>
      `,
        },
        {
            num: '20',
            title: 'No automated individual decisions',
            body: `
        <p>ScreenDeck takes no decisions based solely on automated processing, including profiling, that
        produce legal effects concerning you or similarly significantly affect you (Art. 21 FADP /
        Art. 22 GDPR). Which content appears on which screen follows exclusively the rules set by the
        tenant (schedule, order, validity period) and evaluates no personal characteristics.</p>
      `,
        },
        {
            num: '21',
            title: 'Changes to this policy',
            body: `
        <p>We review this policy regularly and adapt it when the legal position or our processing changes.
        The version in force is available at
        <a href="https://www.gastro-management.ch/privacy?lang=en#screendeck">gastro-management.ch/privacy</a>.
        We inform registered users of material changes by email or through a prominent notice in the
        application. The date of the last revision appears in the page header.</p>
      `,
        },
    ],
};

export const SCREENDECK_PRIVACY: LegalDocSet = { de: DE, en: EN };
